/*
 * Gallery page
 *
 * Shows two categories of photos (Fights / Training) fetched from the backend
 * and displayed in a responsive layout
 *
 * Contract / expectations:
 * - Backend endpoints: '/fight-photos/' and '/training-photos/' return arrays
 *   of objects with at least { id, src, title } or similar fields.
 * - 'resolveSrc' normalizes relative and absolute URLs into a usable image src.
 * - Modal navigation supports ArrowLeft/ArrowRight/Escape and basic swipe
 *   gestures on touch devices.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Modal, Box, IconButton, ToggleButtonGroup, ToggleButton
} from '@mui/material';
import { styled } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import config from '../config'; 
import { Helmet } from 'react-helmet';
import api from '../api';
const url = config.API_URL;

const MasonryContainer = styled('div')(() => ({
  columnCount: 4,
  columnGap: '1em',
  '@media (max-width: 1200px)': {
    columnCount: 3,
  },
  '@media (max-width: 900px)': {
    columnCount: 2,
  },
  '@media (max-width: 600px)': {
    columnCount: 1,
  },
}));

const GalleryItem = styled('div')(() => ({
  breakInside: 'avoid',
  marginBottom: '1em',
  position: 'relative',
  overflow: 'hidden',
  border: '2px solid #9BC9F5',
  borderRadius: 8,
  transition: 'transform 0.3s ease-in-out',
  cursor: 'pointer',
  boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
  '&:hover img': {
    transform: 'scale(1.05)',
    filter: 'grayscale(0%)',
  },
  '&:hover .overlay': {
    opacity: 1,
  },
}));

const GalleryImage = styled('img')(() => ({
  width: '100%',
  objectFit: 'cover',
  filter: 'grayscale(100%)',
  transition: 'transform 0.3s ease-in-out, filter 0.3s ease-in-out',
}));

const Overlay = styled('div')(() => ({
  position: 'absolute',
  bottom: 0,
  width: '100%',
  background: 'rgba(0, 0, 0, 0.75)',
  color: '#9BC9F5',
  padding: '8px',
  fontSize: '0.9rem',
  fontWeight: 500,
  textTransform: 'capitalize',
  opacity: 0,
  transition: 'opacity 0.3s ease-in-out',
}));

const ModalImage = styled('img')(() => ({
  maxWidth: '90vw',
  maxHeight: '90vh',
  objectFit: 'contain',
  borderRadius: '10px',
  userSelect: 'none',
}));

function Gallery() {
  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null);
  // separate lists for fight and training images
  const [fightImages, setFightImages] = useState([]);
  const [trainingImages, setTrainingImages] = useState([]);
  const [tab, setTab] = useState('fights');
  const [showGrid, setShowGrid] = useState(true);

  useEffect(() => {
    // fetch both fight and training photos in parallel using centralized api
    Promise.all([
      api.get('/fight-photos/').then(r => (r && r.data) ? r.data : []),
      api.get('/training-photos/').then(r => (r && r.data) ? r.data : []),
    ])
      .then(([fights, training]) => {
        setFightImages(fights || []);
        // shuffle training images so they display in random order
        const shuffle = (arr) => {
          if (!Array.isArray(arr)) return [];
          const a = arr.slice();
          for (let i = a.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [a[i], a[j]] = [a[j], a[i]];
          }
          return a;
        };
        setTrainingImages(shuffle(training || []));
      })
      .catch((err) => console.error('Failed to load gallery images', err));
  }, []);

  const handleOpen = useCallback((index) => {
    setCurrentIndex(index);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setCurrentIndex(null);
  }, []);

  // compute active images array based on selected tab
  const activeImages = tab === 'fights' ? fightImages : trainingImages;

  // Helper to ensure image src is an absolute URL. The API sometimes returns
  // an absolute URL (http://...) and sometimes a relative path (photos/...).
  // Normalize both so the <img> src always works in the browser.
  const resolveSrc = useCallback((src, optimize = false) => {
    if (!src) return null;
    if (typeof src !== 'string') return null;
    const trimmed = src.trim();
    let resolvedUrl;
    
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('//')) {
      resolvedUrl = trimmed;
    } else {
      // If the src already begins with "/" keep it, otherwise add one.
      const path = trimmed.startsWith('/') ? trimmed : '/' + trimmed;
      resolvedUrl = url + path;
    }
    
    // Optimize images from Cloudinary
    if (optimize && resolvedUrl.includes('cloudinary.com')) {
      // For thumbnails: smaller size, auto format/quality
      resolvedUrl = resolvedUrl.replace('/upload/', '/upload/f_auto,q_auto,w_800,c_limit/');
    }
    
    return resolvedUrl;
  }, []);

  const handleNext = useCallback(() => {
    if (!activeImages.length) return;
    setCurrentIndex((prev) => (prev + 1) % activeImages.length);
  }, [activeImages.length]);

  const handlePrev = useCallback(() => {
    if (!activeImages.length) return;
    setCurrentIndex((prev) => (prev - 1 + activeImages.length) % activeImages.length);
  }, [activeImages.length]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'Escape') handleClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, handleNext, handlePrev, handleClose]);

  useEffect(() => {
    if (!open) return;

    let startX = null;

    const handleTouchStart = (e) => {
      startX = e.touches[0].clientX;
    };

    const handleTouchEnd = (e) => {
      if (startX === null) return;
      const endX = e.changedTouches[0].clientX;
      const diff = startX - endX;
      if (Math.abs(diff) > 50) {
        diff > 0 ? handleNext() : handlePrev();
      }
      startX = null;
    };

    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [open, handleNext, handlePrev]);

  return (
    <Container maxWidth="xl" sx={{ backgroundColor: '#111', color: '#fff', py: 6 }}>
      <Helmet>
        <title>Photo Gallery — Kingdom MMA Edmonton (Fights & Training)</title>
        <meta name="description" content="Browse photos from fights and training sessions at Kingdom MMA. See our coaches and training environment to get a feel for the gym before you visit." />
        <link rel="canonical" href="https://www.kingdommixedmartialarts.ca/gallery" />
        <meta property="og:title" content="Gallery — Kingdom MMA and Fitness" />
        <meta property="og:description" content="Fight and training photos from Kingdom MMA and Fitness." />
        <meta property="og:url" content="https://www.kingdommixedmartialarts.ca/gallery" />
  <meta property="og:image" content="https://www.kingdommixedmartialarts.ca/logo-no-text.svg" />
      </Helmet>
      <Typography variant="h3" align="left" sx={{ mb: 2, fontWeight: 'bold', letterSpacing: 2 }}>
        Gallery
      </Typography>

      {/* Toggle control to switch categories (Fights / Training)*/}
      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={tab}
          exclusive
          onChange={(e, v) => {
            if (!v) return; 
            setShowGrid(false);
            setTimeout(() => {
              setTab(v);
              setOpen(false);
              setCurrentIndex(null);
              setShowGrid(true);
            }, 160);
          }}
          size="small"
          sx={{ display: 'flex', justifyContent: 'center' }}
        >
          <ToggleButton
            value="fights"
            sx={{
              color: '#fff',
              textTransform: 'uppercase',
              fontFamily: 'Oswald, sans-serif',
              fontWeight: 600,
              px: 2,
              '&:hover': { transform: 'scale(1.05)', backgroundColor: 'rgba(255,255,255,0.08)' },
              '&.Mui-selected': {
                color: '#fff',
                backgroundColor: 'rgba(255,255,255,0.08)',
                fontWeight: 'bold',
                transform: 'scale(1.05)'
              }
            }}
          >
            Fights
          </ToggleButton>
          <ToggleButton
            value="training"
            sx={{
              color: '#fff',
              textTransform: 'uppercase',
              fontFamily: 'Oswald, sans-serif',
              fontWeight: 600,
              px: 2,
              '&:hover': { transform: 'scale(1.05)', backgroundColor: 'rgba(255,255,255,0.08)' },
              '&.Mui-selected': {
                color: '#fff',
                backgroundColor: 'rgba(255,255,255,0.08)',
                fontWeight: 'bold',
                transform: 'scale(1.05)'
              }
            }}
          >
            Training
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ transition: 'opacity 220ms ease, transform 220ms ease', opacity: showGrid ? 1 : 0, transform: showGrid ? 'translateY(0)' : 'translateY(6px)' }}>
        <MasonryContainer>
          {activeImages.map((img, index) => {
            const s = resolveSrc(img.src, true); // optimize thumbnails
            if (!s) {
              console.warn('Gallery: missing src for image', img);
            }
            const altText = img.alt_text || img.alt || img.title || '';
            const caption = img.caption || img.title || '';
            return (
              <GalleryItem key={img.id || index} onClick={() => handleOpen(index)}>
                <GalleryImage 
                  src={s || ''} 
                  alt={altText} 
                  loading={index === 0 ? "eager" : "lazy"}
                  fetchpriority={index === 0 ? "high" : undefined}
                />
                <Overlay className="overlay">{caption}</Overlay>
              </GalleryItem>
            );
          })}
        </MasonryContainer>
      </Box>

      <Modal open={open} onClose={handleClose}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
          bgcolor="rgba(0,0,0,0.95)"
          position="relative"
          px={2}
        >
          {currentIndex !== null && activeImages[currentIndex] && (
            <>
              {(() => {
                let modalSrc = resolveSrc(activeImages[currentIndex].src) || '';
                // Optimize modal images from Cloudinary (higher quality but still optimized)
                if (modalSrc.includes('cloudinary.com')) {
                  modalSrc = modalSrc.replace('/upload/', '/upload/f_auto,q_auto,w_1920,c_limit/');
                }
                return (
                  <ModalImage
                    src={modalSrc}
                    alt={(activeImages[currentIndex].alt_text || activeImages[currentIndex].alt || activeImages[currentIndex].title) || ''}
                  />
                );
              })()}
              <IconButton
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  top: 20,
                  right: 20,
                  color: '#9BC9F5',
                  backgroundColor: 'rgba(0,0,0,0.6)',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                }}
              >
                <CloseIcon fontSize="large" />
              </IconButton>

              <IconButton
                onClick={handlePrev}
                sx={{
                  position: 'absolute',
                  left: 20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9BC9F5',
                  backgroundColor: 'rgba(0,0,0,0.4)',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>

              <IconButton
                onClick={handleNext}
                sx={{
                  position: 'absolute',
                  right: 20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9BC9F5',
                  backgroundColor: 'rgba(0,0,0,0.4)',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Modal>
    </Container>
  );
}

export default Gallery;
